import "./styles/MenuItemAdmin.scss";
import React, {ChangeEvent, useState} from "react";
import {MAX_MENU_LVL, MenuItem} from "navigation-common/types/navigation-types";
import Button, {IconButton} from '@atlaskit/button/new';
import {Checkbox} from '@atlaskit/checkbox';
import Tooltip from '@atlaskit/tooltip';
import {navigationHelperService} from "../../api/NavigationHelperService";
import MediaServicesScaleLargeIcon from "@atlaskit/icon/glyph/media-services/scale-large";
import IconPicker from "./IconPicker";
import classNames from "classnames";
import LinkInputField from "./LinkInputField";
import {NameItemInputField} from "./NameItemInputField";
import {CqlInputField} from "./CqlInputField";
import Toggle from "@atlaskit/toggle";
import DeleteIcon from '@atlaskit/icon/core/migration/delete--editor-remove';
import {warnFlag} from "../../model/flags";
import {useFlags} from "@atlaskit/flag";
import TableRowAddBelowIcon from "@atlaskit/icon/core/table-row-add-below";
import InlineDialog from "@atlaskit/inline-dialog";
import AddIcon from '@atlaskit/icon/utility/add';

interface MenuItemAdminProps {
    menuItem: MenuItem,
    changeTrigger: (menuItem: MenuItem) => void,
    addNewMenuItemTrigger: (insert: boolean, menuItem: MenuItem, newMenuItem: MenuItem) => void,
    deleteMenuItemTrigger: (menuItem: MenuItem) => void,
    changeToggle: boolean,
    pageTopAnchor: HTMLDivElement | null,
    newMenuItemKey: string,
    setIsAutocompleteOpen: (state: boolean) => void,
    hasCQLFeature?: boolean,
    menuKey: string,
    openImageModal: (menuItem: MenuItem) => void,
}

function MenuItemAdmin(props: Readonly<MenuItemAdminProps>) {
    const [dialogShown, setDialogShown] = React.useState<boolean>(false);
    const [isCqlEnabled] = useState<boolean>(props.menuItem.cqlEnabled || false);
    const {showFlag} = useFlags();

    const triggerNameChange = (value: string) => {
        props.menuItem.name = value;
        props.changeTrigger(props.menuItem);
    }

    const triggerLinkChange = (value: string) => {
        props.menuItem.link = value;
        props.changeTrigger(props.menuItem);
    }

    function triggerCqlChange(cqlString: string) {
        props.menuItem.cqlString = cqlString;
        props.changeTrigger(props.menuItem);

    }

    function triggerCqlEnabledChange(cqlEnabled: boolean) {
        props.menuItem.cqlEnabled = cqlEnabled;
        props.changeTrigger(props.menuItem);
    }

    const triggerNewTabChange = (event: ChangeEvent<HTMLInputElement>) => {
        props.menuItem.openInNewTab = event.target.checked;
        props.changeTrigger(props.menuItem);
    }

    const triggerIconChange = (icon: string) => {
        props.menuItem.icon = icon;
        props.changeTrigger(props.menuItem);
    }

    const addSubMenuItem = () => {
        const newLevel = props.menuItem.level + 1;
        const oldKey = props.menuItem.subMenuItems && props.menuItem.subMenuItems.length > 0 ? props.menuItem.subMenuItems[props.menuItem.subMenuItems.length - 1].key : props.menuItem.key;
        let newKey = navigationHelperService.buildNextKey(oldKey, newLevel);
        let newMenuItem = navigationHelperService.buildNewMenuItem(newKey, newLevel);
        if (!props.menuItem.subMenuItems) {
            props.menuItem.subMenuItems = [];
        }
        props.menuItem.subMenuItems.push(newMenuItem);
        props.addNewMenuItemTrigger(false, props.menuItem, newMenuItem);
    }

    const addSameLvlMenuItem = () => {
        const newLevel = props.menuItem.level;
        let newKey = navigationHelperService.buildKeyAtIndex(newLevel, props.menuItem.key);
        let newMenuItem = navigationHelperService.buildNewMenuItem(newKey, newLevel);
        props.addNewMenuItemTrigger(true, props.menuItem, newMenuItem);
    }

    const deletionConfirmed = () => {
        props.deleteMenuItemTrigger(props.menuItem);
        setDialogShown(false);
    }

    const validateCqlFeature = () => {
        if (!props.hasCQLFeature) {
            showFlag(warnFlag({title: "Update your Navigation Menus to start using the new CQL Feature."}));
        }
    }

    return (
        <div className={"menu-item-container"}>
            <div className={`nav-admin-group level-${props.menuItem.level}`}>
            <span className={"nav-admin-item level-id"}>
                {props.menuItem.key}
            </span>
                <span className={"nav-admin-item icon"}>
                {props.menuItem.level === 1
                    ? <IconPicker iconValue={props.menuItem.icon ?? ''}
                                  iconSelected={icon => triggerIconChange(icon)}></IconPicker>
                    : ''
                }
            </span>
                <span className={"nav-admin-item name"}>
                <NameItemInputField menuItem={props.menuItem} changeTrigger={triggerNameChange} newMenuItemKey={props.newMenuItemKey}/>
            </span>
                <span className={"nav-admin-item cqlSwitch"}>
                    <Tooltip position={"bottom"}
                             content={<div className={`adminTooltip`}>Use CQL to create dynamic links.<br/>
                                 Check our <a href={"https://help-seibertmedia.atlassian.net/wiki/x/AQDsGw"} target={"_blank"}
                                              rel="noreferrer">documentation</a> for the most common use cases and queries</div>}>
                        <div onClick={validateCqlFeature}><Toggle defaultChecked={isCqlEnabled} onChange={() => triggerCqlEnabledChange(!isCqlEnabled)}
                                                                  isDisabled={!props.hasCQLFeature}/></div>
                    </Tooltip>
                </span>
                <span className={"nav-admin-item link"}>
                {isCqlEnabled ? <CqlInputField menuItem={props.menuItem} changeTrigger={triggerCqlChange}/> :
                    <LinkInputField menuItem={props.menuItem} linkChangeTrigger={triggerLinkChange} setIsAutocompleteOpen={props.setIsAutocompleteOpen}/>}
            </span>
                <span className={"nav-admin-item newtab"}>
                    <Tooltip content="Open link in new browser tab">
                    <Checkbox
                        isChecked={props.menuItem.openInNewTab}
                        value="default checkbox"
                        label=""
                        onChange={(e) => triggerNewTabChange(e)}
                        name="checkbox-default"
                        testId="cb-default"
                    />
                    </Tooltip>
            </span>

                <span className={"nav-admin-item image"}>
                    {props.menuItem.level === 1 &&
                        <Tooltip content="Edit 1st level background image">
                            <div className={classNames("image-item", {"image-set": props.menuItem.imageUrl})}
                                 style={{backgroundImage: `url(${props.menuItem.imageUrl})`}}
                                 onClick={() => props.openImageModal(props.menuItem)}>
                                {!props.menuItem.imageUrl &&
                                    <MediaServicesScaleLargeIcon size={"small"} label="Add background image"/>}
                            </div>
                        </Tooltip>
                    }
                    {props.menuItem.level === 2 && <Tooltip content="Edit 2nd level teaser image">
                        <div className={classNames("image-item", {"image-set": props.menuItem.imageUrl})}
                             style={{backgroundImage: `url(${props.menuItem.imageUrl})`}}
                             onClick={() => props.openImageModal(props.menuItem)}>
                            {!props.menuItem.imageUrl &&
                                <MediaServicesScaleLargeIcon size={"small"} label="Add teaser image"/>
                            }
                        </div>
                    </Tooltip>
                    }
            </span>
                <span className={"nav-admin-item actions"}>
                    <Tooltip position={"bottom"} content={"Add item on same level"}>
                        <IconButton icon={AddIcon} label={"Add Item"} onClick={addSameLvlMenuItem}/>
                    </Tooltip>
                    <Tooltip position={"bottom"} content={"Add sub-item"}>
                        <IconButton icon={TableRowAddBelowIcon} label={"Add Sub Item"} onClick={addSubMenuItem}
                                    isDisabled={props.menuItem.level >= MAX_MENU_LVL}/>
                    </Tooltip>
                    <InlineDialog
                        content={
                            <div className={"delete-confirm-dialog"}>
                                <p>The menu item including all sub menu items will be deleted.</p>
                                <div className={"delete-confirm-dialog-actions"}>
                                    <Button appearance="subtle" onClick={() => setDialogShown(false)}>Cancel</Button>
                                    <div className={"save-button"}><Button appearance="danger" onClick={() => deletionConfirmed()} autoFocus>Delete</Button>
                                    </div>
                                </div>
                            </div>
                        }
                        isOpen={dialogShown}
                        placement={"bottom"}
                        onClose={() => {
                            setDialogShown(false)
                        }}
                    >
            <IconButton icon={DeleteIcon} label={"Remove menu item"} onClick={() => setDialogShown(true)}/>
        </InlineDialog>
            </span>
            </div>
        </div>
    )
}

export default MenuItemAdmin;
